import { ReactNode, useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "next-i18next";
import { Button, Dropdown, Tooltip } from "antd";

import { poppins } from "@/components/common/fonts";
import {
  IconDiamond16,
  IconShareDownloadSubtitles,
  IconShareDownloadVideo,
  IconShareExport4k,
} from "@/components/common/Icons";

export const DownloadDropdown = ({
  onExport4k,
  onDownloadVideo,
  onDownloadSrt,

  hasSrt,
  isOwner,
  canExport4k,
  canDownloadSrt,

  isLoadingExport4k,
  isLoadingDownloadVideo,
  isLoadingDownloadSrt,

  children,
}: {
  onExport4k?: () => void;
  onDownloadVideo?: () => void;
  onDownloadSrt?: () => void;

  hasSrt?: boolean;
  isOwner?: boolean;
  canExport4k?: { is: boolean; why: string };
  canDownloadSrt?: boolean;

  isLoadingExport4k?: boolean;
  isLoadingDownloadVideo?: boolean;
  isLoadingDownloadSrt?: boolean;

  children?: ReactNode;
}) => {
  const [isOpened, setIsOpened] = useState(false);

  const handleExport4k = useCallback(() => {
    onExport4k?.();
    setIsOpened(false);
  }, [onExport4k]);

  const handleDownloadVideo = useCallback(() => {
    onDownloadVideo?.();
    setIsOpened(false);
  }, []);

  const handleDownloadSrt = useCallback(() => {
    onDownloadSrt?.();
    setIsOpened(false);
  }, [onDownloadSrt]);

  return (
    <StyledDropdown
      open={isOpened}
      trigger={["click"]}
      onOpenChange={(open) => setIsOpened(open)}
      dropdownRender={() => (
        <DropdownContent
          onExport4k={handleExport4k}
          onDownloadVideo={handleDownloadVideo}
          onDownloadSrt={handleDownloadSrt}
          hasSrt={hasSrt}
          isOwner={isOwner}
          canExport4k={canExport4k}
          canDownloadSrt={canDownloadSrt}
          isLoadingExport4k={isLoadingExport4k}
          isLoadingDownloadVideo={isLoadingDownloadVideo}
          isLoadingDownloadSrt={isLoadingDownloadSrt}
        />
      )}
      destroyPopupOnHide
      placement="bottomRight"
      align={{
        offset: [0, 13],
      }}
    >
      {children}
    </StyledDropdown>
  );
};

const StyledDropdown = styled(Dropdown)``;

const DropdownContent = ({
  onExport4k,
  onDownloadVideo,
  onDownloadSrt,

  hasSrt,
  isOwner,
  canExport4k,
  canDownloadSrt,

  isLoadingExport4k,
  isLoadingDownloadVideo,
  isLoadingDownloadSrt,
}: {
  onExport4k?: () => void;
  onDownloadVideo?: () => void;
  onDownloadSrt?: () => void;

  hasSrt?: boolean;
  isOwner?: boolean;
  canExport4k?: { is: boolean; why: string };
  canDownloadSrt?: boolean;

  isLoadingExport4k?: boolean;
  isLoadingDownloadVideo?: boolean;
  isLoadingDownloadSrt?: boolean;
}) => {
  const { t } = useTranslation("common");

  const tooltipTitle = useMemo(() => {
    if (!canDownloadSrt) {
      return;
    }

    if (!isOwner) {
      return t("Team only. Available to members in the same team.");
    }

    if (!hasSrt) {
      return t("No subtitle file has been generated for this video.");
    }
  }, [canDownloadSrt, isOwner, hasSrt, t]);

  const disabledDownloadSrt = useMemo(() => {
    if (!canDownloadSrt) {
      return false;
    }

    return !isOwner || !hasSrt;
  }, [canDownloadSrt, isOwner, hasSrt]);

  return (
    <ContentContainer>
      <DownloadButton
        type="text"
        onClick={onExport4k}
        disabled={canExport4k?.why === "resolution"}
        loading={isLoadingExport4k}
        icon={<IconShareExport4k width={20} height={20} className="current-color" />}
      >
        {t("Export in 4K Video")}
        {(canExport4k?.why === "plan") && <IconDiamond16 />}
      </DownloadButton>
      <DownloadButton
        type="text"
        onClick={onDownloadVideo}
        loading={isLoadingDownloadVideo}
        icon={<IconShareDownloadVideo className="current-color" />}
      >
        {t("Download Video (.mp4)")}
      </DownloadButton>
      <Tooltip title={tooltipTitle} mouseEnterDelay={0.5} placement="bottom">
        <DownloadButton
          type="text"
          onClick={onDownloadSrt}
          disabled={disabledDownloadSrt}
          loading={isLoadingDownloadSrt}
          icon={<IconShareDownloadSubtitles className="current-color" />}
        >
          {t("Download Subtitles (.srt)")}
          {!disabledDownloadSrt && !canDownloadSrt && <IconDiamond16 />}
        </DownloadButton>
      </Tooltip>
    </ContentContainer>
  );
};

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;

  min-width: 230px;
  max-height: calc(100vh - 70px);
  overflow: auto;
  box-sizing: border-box;

  border-radius: 4px;
  background: #fff;
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.1);
`;

const DownloadButton = styled(Button)`
  justify-content: flex-start;
  gap: 10px;

  height: auto;
  padding: 8px 15px;

  color: var(--sub-333, #333);

  /* sub_title_h3 */
  font-family: ${poppins.style.fontFamily};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;

  svg {
    flex-shrink: 0;

    &.current-color {
      & > path {
        fill: currentColor;
      }

      & > g {
        stroke: currentColor;
      }
    }
  }

  .ant-btn-loading-icon {
    justify-content: center;
    width: 20px;
  }
`;
